import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  type LucideIcon,
  ArrowLeft,
  AlertTriangle,
  CheckCircle2,
  RotateCcw,
  Clock,
  Database,
  Server,
  Settings,
  AlertCircle,
} from 'lucide-react';
import { api } from '@shared/services/api';
import { useDialog } from '@shared/hooks/useDialog';
import { useUser } from '@shared/hooks/useUser';
import { Card, Badge, Button } from '@shared/components/ui';
import { getStatusTone, toneToBadgeVariant } from '@shared/config/theme';
import { toneTextClasses } from '@shared/utils/statusTone';
import { formatRelativeTime } from '@shared/utils/formatRelativeTime';
import { normalizeBackupType } from '../utils/backupType';

import { BackupAlertState } from '@features/backups/types/alert-state';

interface BackupConfigData {
  id: string;
  name: string;
  type: string;
  description: string | null;
  agent_name: string;
  agent_status: string;
  schedule?: {
    type: string;
    cron_expression: string;
    interval_value: string;
    timezone: string;
    next_run_at: string;
    is_enabled: boolean;
  };
  storage_destination_id: string;
  storage_destination_name?: string;
  is_enabled: boolean;
  config: Record<string, unknown>;
}

export default function BackupAlertStateDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['backups', 'common']);
  const { showConfirm, showSuccess, showError } = useDialog();
  const { user } = useUser();

  const [alertState, setAlertState] = useState<BackupAlertState | null>(null);
  const [config, setConfig] = useState<BackupConfigData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canRetry, setCanRetry] = useState(false);
  const [resetting, setResetting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setCanRetry(false);
    try {
      const [alertRes, configRes] = await Promise.all([
        api.get<BackupAlertState>(`/backups/configs/${id}/alert-settings`),
        api.get<BackupConfigData>(`/backups/configs/${id}`),
      ]);
      setAlertState(alertRes.data);
      setConfig(configRes.data);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number } };
      const status = axiosErr.response?.status;
      const notFound = status === 404;
      setError(t(notFound ? 'errors.notFound' : 'errors.loadFailed'));
      setCanRetry(!status || status >= 500);
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  // Fetch both APIs in parallel
  useEffect(() => {
    if (!id) return;
    fetchData();
  }, [id, fetchData]);

  const handleReset = async () => {
    if (!id) return;
    const confirmed = await showConfirm(
      t('alertState.resetConfirmTitle'),
      t('alertState.resetConfirmMessage'),
      'warning'
    );
    if (!confirmed) return;

    setResetting(true);
    try {
      await api.post(`/backups/configs/${id}/alert-settings/reset`);
      showSuccess(t('alertState.resetSuccess'), '');
      await fetchData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      showError(t('errors.resetFailed'), axiosErr.response?.data?.error || t('errors.unknown'));
    } finally {
      setResetting(false);
    }
  };

  const formatDuration = (seconds: number) => {
    if (!seconds || seconds < 0) return t('alertState.duration.zeroMinutes');
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const parts: string[] = [];
    if (hours > 0)
      parts.push(
        t(hours === 1 ? 'alertState.duration.hour' : 'alertState.duration.hours', { count: hours })
      );
    if (minutes > 0)
      parts.push(
        t(minutes === 1 ? 'alertState.duration.minute' : 'alertState.duration.minutes', {
          count: minutes,
        })
      );
    return parts.length > 0 ? parts.join(' ') : t('alertState.duration.lessThanMinute');
  };

  const formatDateTime = (value: string) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? '-'
      : new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language, {
          dateStyle: 'medium',
          timeStyle: 'short',
        }).format(date);
  };

  const formatAlertState = (value: string) => {
    const key = value.toLowerCase();
    return ['ok', 'grace_period', 'alerting', 'throttled'].includes(key)
      ? t(`alertState.status.${key}`)
      : value;
  };

  const formatScheduleType = (value: string) =>
    t(`alertState.scheduleTypeValue.${value.toLowerCase()}`, { defaultValue: value });

  /** Glyph per alert state; the colour comes from the shared tone contract. */
  const alertStateIcons: Record<string, LucideIcon> = {
    OK: CheckCircle2,
    GRACE_PERIOD: AlertTriangle,
    ALERTING: AlertTriangle,
    THROTTLED: AlertTriangle,
  };

  const isAdmin = ['admin', 'owner', 'platform_admin'].includes(user?.role?.toLowerCase() || '');

  // Loading state
  if (loading) {
    return (
      <div className="py-20 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  // Error state
  if (error || !alertState) {
    return (
      <Card className="border-state-danger-light/20 bg-state-danger-bg-light dark:border-state-danger-dark/20 dark:bg-state-danger-bg-dark">
        <div
          role="alert"
          className="flex min-h-56 flex-col items-center justify-center gap-4 px-4 py-8 text-center font-bold text-state-danger-light dark:text-state-danger-dark"
        >
          <p className="max-w-2xl break-words">{error || t('errors.notFound')}</p>
          <div className="flex flex-wrap justify-center gap-3">
            {canRetry && (
              <Button variant="outline" className="!min-h-11" onClick={() => void fetchData()}>
                {t('common:actions.retry')}
              </Button>
            )}
            <Button
              variant="outline"
              className="!min-h-11"
              onClick={() => navigate('/backups/configs')}
            >
              {t('alertState.backToConfigs')}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Main render
  return (
    <div className="space-y-8 animate-in fade-in duration-700">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('alertState.backToConfigs')}
            onClick={() => navigate('/backups/configs')}
            className="!min-h-11 min-w-11 p-2 hover:bg-surface-2-light dark:hover:bg-surface-2-dark rounded-xl"
          >
            <ArrowLeft size={20} className="text-text-subtle-light dark:text-text-subtle-dark" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white tracking-tight break-words">
                {alertState.backup_name || config?.name || t('alertState.unnamedConfig')}
              </h1>
              <Badge
                variant={toneToBadgeVariant[getStatusTone(alertState.alert_state)]}
                className="text-xs"
              >
                {formatAlertState(alertState.alert_state)}
              </Badge>
            </div>
            <p className="text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark font-mono mt-1">
              {alertState.backup_config_id}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Alert State Card */}
          <Card className="p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-2xl bg-surface-2-light dark:bg-surface-2-dark flex items-center justify-center">
                {(() => {
                  const AlertIcon = alertStateIcons[alertState.alert_state] ?? AlertCircle;
                  return (
                    <AlertIcon
                      size={48}
                      className={toneTextClasses[getStatusTone(alertState.alert_state)]}
                    />
                  );
                })()}
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-900 dark:text-white">
                  {t('alertState.title')}
                </h2>
                <Badge
                  variant={toneToBadgeVariant[getStatusTone(alertState.alert_state)]}
                  className="mt-1"
                >
                  {formatAlertState(alertState.alert_state)}
                </Badge>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <DetailItem
                  label={t('alertState.settings.consecutiveFailures')}
                  value={String(alertState.consecutive_failures)}
                />
                <DetailItem
                  label={t('alertState.settings.totalDowntime')}
                  value={formatDuration(alertState.total_downtime_seconds)}
                />
              </div>
              <div className="space-y-4">
                <DetailItem
                  label={t('alertState.settings.gracePeriod')}
                  value={`${alertState.grace_period_minutes}m`}
                />
                <DetailItem
                  label={t('alertState.settings.throttleInterval')}
                  value={`${alertState.throttle_interval_minutes}m`}
                />
              </div>
            </div>

            {alertState.failure_reason && (
              <div className="mt-6 p-4 bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 rounded-xl">
                <p className="text-[10px] font-black text-state-danger-light dark:text-state-danger-dark uppercase tracking-widest mb-2">
                  {t('alertState.failureReason')}
                </p>
                <p className="text-sm font-bold text-state-danger-light dark:text-state-danger-dark">
                  {alertState.failure_reason}
                </p>
              </div>
            )}
          </Card>

          {/* Timeline Section */}
          <Card className="p-6">
            <h3 className="text-sm font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark mb-6 flex items-center gap-2">
              <Clock size={14} className="text-brand-600 dark:text-brand-400" />{' '}
              {t('alertState.timeline.title')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <DetailItem
                label={t('alertState.timeline.lastSuccess')}
                value={
                  alertState.last_successful_run_at
                    ? `${formatDateTime(alertState.last_successful_run_at)} (${formatRelativeTime(alertState.last_successful_run_at)})`
                    : '-'
                }
              />
              <DetailItem
                label={t('alertState.timeline.firstFailure')}
                value={
                  alertState.first_failure_at
                    ? `${formatDateTime(alertState.first_failure_at)} (${formatRelativeTime(alertState.first_failure_at)})`
                    : '-'
                }
              />
              <DetailItem
                label={t('alertState.timeline.lastFailure')}
                value={
                  alertState.last_failure_at
                    ? `${formatDateTime(alertState.last_failure_at)} (${formatRelativeTime(alertState.last_failure_at)})`
                    : '-'
                }
              />
              <DetailItem
                label={t('alertState.timeline.lastAlert')}
                value={
                  alertState.last_alert_sent_at
                    ? `${formatDateTime(alertState.last_alert_sent_at)} (${formatRelativeTime(alertState.last_alert_sent_at)})`
                    : '-'
                }
              />
            </div>
          </Card>

          {/* Backup Config Context */}
          {config && (
            <Card className="p-6">
              <h3 className="text-sm font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark mb-6 flex items-center gap-2">
                <Database size={14} className="text-state-info-light dark:text-state-info-dark" />{' '}
                {t('alertState.backupConfig')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <DetailItem label={t('alertState.agentName')} value={config.agent_name || '-'} />
                <DetailItem
                  label={t('alertState.agentStatus')}
                  value={
                    config.agent_status ? (
                      <Badge
                        variant={
                          config.agent_status.toLowerCase() === 'online' ? 'success' : 'neutral'
                        }
                        className="text-[10px]"
                      >
                        {t(
                          `alertState.agentStatusValue.${config.agent_status.toLowerCase()}`,
                          config.agent_status
                        )}
                      </Badge>
                    ) : (
                      '-'
                    )
                  }
                />
                <DetailItem
                  label={t('alertState.backupType')}
                  value={
                    normalizeBackupType(config.type) === 'FILES'
                      ? t('filters.type.files')
                      : normalizeBackupType(config.type) === 'POSTGRESQL'
                        ? t('filters.type.postgresql')
                        : config.type || '-'
                  }
                />
                <DetailItem
                  label={t(
                    config.storage_destination_name
                      ? 'alertState.storageDestination'
                      : 'alertState.storageDestinationId'
                  )}
                  value={config.storage_destination_name || config.storage_destination_id || '-'}
                  mono
                />
                {config.schedule && (
                  <>
                    <DetailItem
                      label={t('alertState.scheduleType')}
                      value={config.schedule.type ? formatScheduleType(config.schedule.type) : '-'}
                    />
                    <DetailItem
                      label={t('alertState.scheduleExpression')}
                      value={
                        config.schedule.type === 'CRON'
                          ? config.schedule.cron_expression
                          : config.schedule.interval_value
                      }
                      mono
                    />
                    <DetailItem
                      label={t('alertState.scheduleTimezone')}
                      value={config.schedule.timezone || '-'}
                    />
                    <DetailItem
                      label={t('alertState.nextRun')}
                      value={
                        config.schedule.next_run_at
                          ? formatDateTime(config.schedule.next_run_at)
                          : '-'
                      }
                    />
                  </>
                )}
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-8">
          {/* Status Summary */}
          <Card className="p-6 border-l-4 border-l-brand-500">
            <h3 className="text-sm font-black uppercase tracking-widest text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
              <Server size={16} className="text-brand-600 dark:text-brand-400" />{' '}
              {t('alertState.summary')}
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center text-xs">
                <span className="font-black text-text-subtle-light dark:text-text-subtle-dark tracking-widest uppercase">
                  {t('alertState.statusLabel')}
                </span>
                <Badge
                  variant={toneToBadgeVariant[getStatusTone(alertState.alert_state)]}
                  className="text-[10px]"
                >
                  {formatAlertState(alertState.alert_state)}
                </Badge>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="font-black text-text-subtle-light dark:text-text-subtle-dark tracking-widest uppercase">
                  {t('alertState.failures')}
                </span>
                <span className="font-bold text-slate-900 dark:text-slate-200">
                  {alertState.consecutive_failures}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="font-black text-text-subtle-light dark:text-text-subtle-dark tracking-widest uppercase">
                  {t('alertState.settings.totalDowntime')}
                </span>
                <span className="font-bold text-slate-900 dark:text-slate-200">
                  {formatDuration(alertState.total_downtime_seconds)}
                </span>
              </div>
            </div>
          </Card>

          {/* Reset Button (admin only) */}
          {isAdmin && (
            <Card className="p-6">
              <h3 className="text-sm font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark mb-4 flex items-center gap-2">
                <Settings size={16} className="text-text-subtle-light dark:text-text-subtle-dark" />{' '}
                {t('alertState.actions')}
              </h3>
              <Button
                variant="outline"
                className="w-full justify-start !min-h-11 border-surface-border-light dark:border-surface-border-dark hover:border-brand-600 dark:hover:border-brand-400 hover:text-brand-600 dark:hover:text-brand-400"
                onClick={handleReset}
                disabled={resetting}
                isLoading={resetting}
              >
                <RotateCcw size={16} className="mr-2" />
                {t('alertState.resetButton')}
              </Button>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailItem({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div>
      <p className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-[0.2em] mb-1.5">
        {label}
      </p>
      <p
        className={`text-sm font-bold text-slate-900 dark:text-white ${mono ? 'font-mono break-all' : ''}`}
      >
        {value}
      </p>
    </div>
  );
}
